import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Descriptions,
  Button,
  Space,
  Tag,
  Spin,
  Alert,
  Modal,
  message,
  Card,
  List,
  Input,
  Switch,
  Divider,
  Typography,
} from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  ArrowLeftOutlined,
  UserAddOutlined,
  CloseCircleOutlined,
  UndoOutlined,
  PlusOutlined,
  LockOutlined,
  GlobalOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  getTicket,
  deleteTicket,
  assignTicket,
  closeTicket,
  reopenTicket,
  getTicketComments,
  createTicketComment,
  deleteTicketComment,
  type Ticket,
  type TicketComment,
} from '@/api/ticketApi';
import { useAuth } from '@/hooks/useAuth';

const { TextArea } = Input;
const { Text } = Typography;

const STATUS_COLORS: Record<string, string> = {
  open: 'blue',
  in_progress: 'processing',
  pending: 'orange',
  resolved: 'green',
  closed: 'default',
};

const PRIORITY_COLORS: Record<string, string> = {
  low: 'default',
  medium: 'blue',
  high: 'orange',
  critical: 'red',
};

const TYPE_COLORS: Record<string, string> = {
  inquiry: 'cyan',
  issue: 'volcano',
  feature_request: 'purple',
};

export default function TicketDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [comments, setComments] = useState<TicketComment[]>([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [assigneeId, setAssigneeId] = useState('');

  const canWrite = hasPermission('tickets', 'write');
  const canDelete = hasPermission('tickets', 'delete');

  const loadComments = useCallback((ticketId: string) => {
    setCommentsLoading(true);
    getTicketComments(ticketId, { from: 0, size: 200 })
      .then((res) => setComments(res.items))
      .catch(() => setComments([]))
      .finally(() => setCommentsLoading(false));
  }, []);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setError(null);
    getTicket(id)
      .then((data) => {
        setTicket(data);
        loadComments(id);
      })
      .catch((err) => setError(err instanceof Error ? err.message : t('app.not_found')))
      .finally(() => setLoading(false));
  }, [id, t, loadComments]);

  const handleDelete = useCallback(() => {
    if (!id) return;
    Modal.confirm({
      title: t('app.confirm'),
      content: t('tickets.confirm_delete'),
      okText: t('app.yes'),
      cancelText: t('app.no'),
      okType: 'danger',
      onOk: async () => {
        try {
          await deleteTicket(id);
          message.success(t('tickets.delete_success'));
          navigate('/tickets');
        } catch {
          message.error(t('app.error'));
        }
      },
    });
  }, [id, t, navigate]);

  const handleLifecycle = useCallback(
    async (action: 'close' | 'reopen') => {
      if (!id) return;
      try {
        if (action === 'close') await closeTicket(id);
        else await reopenTicket(id);
        message.success(t(`tickets.${action}_success`));
        const updated = await getTicket(id);
        setTicket(updated);
      } catch {
        message.error(t('app.error'));
      }
    },
    [id, t]
  );

  const handleAssign = useCallback(async () => {
    if (!id || !assigneeId.trim()) return;
    try {
      await assignTicket(id, { assigned_to: assigneeId.trim() });
      message.success(t('tickets.assign_success'));
      const updated = await getTicket(id);
      setTicket(updated);
      setAssigneeId('');
    } catch {
      message.error(t('app.error'));
    }
  }, [id, assigneeId, t]);

  const handleAddComment = useCallback(async () => {
    if (!id || !newComment.trim()) return;
    setSubmitting(true);
    try {
      await createTicketComment(id, {
        content: newComment.trim(),
        is_internal: isInternal,
      });
      message.success(t('tickets.comments.add_success'));
      setNewComment('');
      setIsInternal(false);
      loadComments(id);
    } catch {
      message.error(t('app.error'));
    } finally {
      setSubmitting(false);
    }
  }, [id, newComment, isInternal, t, loadComments]);

  const handleDeleteComment = useCallback(
    (commentId: string) => {
      if (!id) return;
      Modal.confirm({
        title: t('app.confirm'),
        content: t('tickets.comments.confirm_delete'),
        okText: t('app.yes'),
        cancelText: t('app.no'),
        okType: 'danger',
        onOk: async () => {
          try {
            await deleteTicketComment(id, commentId);
            message.success(t('tickets.comments.delete_success'));
            loadComments(id);
          } catch {
            message.error(t('app.error'));
          }
        },
      });
    },
    [id, t, loadComments]
  );

  if (loading) return <Spin size="large" />;
  if (error) return <Alert type="error" message={error} />;
  if (!ticket) return <Alert type="warning" message={t('app.not_found')} />;

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/tickets')}>
          {t('app.back')}
        </Button>
        {canWrite && (
          <Button icon={<EditOutlined />} onClick={() => navigate(`/tickets/${id}/edit`)}>
            {t('app.edit')}
          </Button>
        )}
        {canDelete && (
          <Button danger icon={<DeleteOutlined />} onClick={handleDelete}>
            {t('app.delete')}
          </Button>
        )}
        {ticket.status !== 'closed' && ticket.status !== 'resolved' && canWrite && (
          <Button icon={<CloseCircleOutlined />} onClick={() => handleLifecycle('close')}>
            {t('tickets.action.close')}
          </Button>
        )}
        {(ticket.status === 'resolved' || ticket.status === 'closed') && canWrite && (
          <Button icon={<UndoOutlined />} onClick={() => handleLifecycle('reopen')}>
            {t('tickets.action.reopen')}
          </Button>
        )}
      </Space>

      <h2>{t('tickets.detail_title')}</h2>

      <Descriptions bordered column={2}>
        <Descriptions.Item label={t('tickets.field.subject')} span={2}>
          {ticket.subject}
        </Descriptions.Item>
        <Descriptions.Item label={t('tickets.field.description')} span={2}>
          {ticket.description || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('tickets.field.type')}>
          <Tag color={TYPE_COLORS[ticket.type] ?? 'default'}>
            {t(`tickets.type_options.${ticket.type}`, ticket.type)}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label={t('tickets.field.priority')}>
          <Tag color={PRIORITY_COLORS[ticket.priority] ?? 'default'}>
            {t(`tickets.priority_options.${ticket.priority}`, ticket.priority)}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label={t('tickets.field.status')}>
          <Tag color={STATUS_COLORS[ticket.status] ?? 'default'}>
            {t(`tickets.status_options.${ticket.status}`, ticket.status)}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label={t('tickets.field.assigned_to')}>
          {ticket.assigned_to || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('tickets.field.customer_id')}>
          {ticket.customer_id}
        </Descriptions.Item>
        <Descriptions.Item label={t('tickets.field.contact_id')}>
          {ticket.contact_id || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('tickets.field.tags')} span={2}>
          {ticket.tags?.length > 0 ? ticket.tags.map((tag) => <Tag key={tag}>{tag}</Tag>) : '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('tickets.field.resolution')} span={2}>
          {ticket.resolution || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('tickets.field.sla_due_at')}>
          {ticket.sla_due_at ? new Date(ticket.sla_due_at).toLocaleString() : '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('tickets.field.first_response_at')}>
          {ticket.first_response_at ? new Date(ticket.first_response_at).toLocaleString() : '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('tickets.field.resolved_at')}>
          {ticket.resolved_at ? new Date(ticket.resolved_at).toLocaleString() : '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('tickets.field.branch_id')}>
          {ticket.branch_id || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('tickets.field.created_by')}>
          {ticket.created_by}
        </Descriptions.Item>
        <Descriptions.Item label={t('tickets.field.created_at')}>
          {new Date(ticket.created_at).toLocaleString()}
        </Descriptions.Item>
        <Descriptions.Item label={t('tickets.field.updated_by')}>
          {ticket.updated_by}
        </Descriptions.Item>
        <Descriptions.Item label={t('tickets.field.updated_at')}>
          {new Date(ticket.updated_at).toLocaleString()}
        </Descriptions.Item>
      </Descriptions>

      {/* Assign section */}
      {canWrite && (
        <Card size="small" style={{ marginTop: 16 }}>
          <Space>
            <Input
              placeholder={t('tickets.field.assigned_to')}
              value={assigneeId}
              onChange={(e) => setAssigneeId(e.target.value)}
              style={{ width: 200 }}
            />
            <Button icon={<UserAddOutlined />} onClick={handleAssign} disabled={!assigneeId.trim()}>
              {t('tickets.action.assign')}
            </Button>
          </Space>
        </Card>
      )}

      {/* Comments section */}
      <Divider />
      <h3>{t('tickets.comments.title')}</h3>

      {canWrite && (
        <Card size="small" style={{ marginBottom: 16 }}>
          <TextArea
            rows={3}
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder={t('tickets.comments.add_comment')}
          />
          <Space style={{ marginTop: 8 }}>
            <Switch
              checked={isInternal}
              onChange={setIsInternal}
              checkedChildren={<LockOutlined />}
              unCheckedChildren={<GlobalOutlined />}
            />
            <Text type="secondary">
              {isInternal ? t('tickets.comments.internal_note') : t('tickets.comments.external_reply')}
            </Text>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              loading={submitting}
              disabled={!newComment.trim()}
              onClick={handleAddComment}
            >
              {t('tickets.comments.add_comment')}
            </Button>
          </Space>
        </Card>
      )}

      <List
        loading={commentsLoading}
        dataSource={comments}
        locale={{ emptyText: t('tickets.comments.no_comments') }}
        renderItem={(comment) => (
          <List.Item
            actions={
              canDelete
                ? [
                    <Button
                      type="link"
                      danger
                      size="small"
                      icon={<DeleteOutlined />}
                      onClick={() => handleDeleteComment(comment.id)}
                    />,
                  ]
                : undefined
            }
          >
            <List.Item.Meta
              title={
                <Space>
                  <Text strong>{comment.author_id}</Text>
                  {comment.is_internal && (
                    <Tag color="gold" icon={<LockOutlined />}>
                      {t('tickets.comments.is_internal')}
                    </Tag>
                  )}
                  <Text type="secondary">{new Date(comment.created_at).toLocaleString()}</Text>
                </Space>
              }
              description={comment.content}
            />
          </List.Item>
        )}
      />
    </div>
  );
}
